import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EndpointService } from '../../../../../../@shared/endpoints.service';
import { CompanyResponse, CompanyServiceResponse } from '../../../../../../@shared/models/response.module';
import { ToastService } from '../../../../../../@core/services/toast.service';

@Component({
  selector: 'app-add-service',
  standalone: false,
  templateUrl: './add-service.html',
  styleUrl: './add-service.css'
})
export class AddService implements OnInit {
  @Input() company: CompanyResponse;
  @Input() editMode: boolean = false;
  @Input() service: CompanyServiceResponse | null = null;

  form: FormGroup;
  saving: boolean = false;

  vatOptions: { value: number; label: string }[] = [];

  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private endpointService: EndpointService,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {
    this.form = this.fb.group({
      service_name: ['', [Validators.required, Validators.maxLength(100)]],
      service_description: ['', [Validators.maxLength(500)]],
      price: [0, [Validators.required, Validators.min(0)]],
      currency: ['NOK'],
      vat_rate: [0]
    });
  }

  ngOnInit() {
    this.initVatOptions();
    this.initializeCurrency();
    if (this.editMode && this.service) {
      this.form.patchValue({
        service_name: this.service.service_name,
        service_description: this.service.service_description || '',
        price: this.service.price_cents / 100,
        currency: this.service.currency || this.company?.default_currency || 'NOK',
        vat_rate: this.service.vat_rate || 0
      });
    }
  }

  initializeCurrency() {
    // Set currency from company's default_currency
    if (this.company?.default_currency) {
      this.form.get('currency')?.setValue(this.company.default_currency);
    }
  }

  initVatOptions() {
    this.vatOptions = [
      { value: 0, label: this.translateService.instant('companyProfile.addService.noVat') },
      { value: 12, label: this.translateService.instant('companyProfile.addService.vat12') },
      { value: 25, label: this.translateService.instant('companyProfile.addService.vat25') }
    ];
  }

  close() {
    this.activeModal.dismiss();
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.error(this.translateService.instant('companyProfile.addService.validationError'));
      return;
    }

    this.saving = true;
    const formValue = this.form.value;
    const priceCents = Math.round(formValue.price * 100);

    if (this.editMode && this.service) {
      this.endpointService.csr_3(this.service.service_id, {
        service_name: formValue.service_name,
        service_description: formValue.service_description,
        price_cents: priceCents,
        currency: formValue.currency,
        vat_rate: formValue.vat_rate
      }).subscribe({
        next: () => {
          this.saving = false;
          this.toastService.success(this.translateService.instant('companyProfile.addService.updateSuccess'));
          this.activeModal.close(true);
        },
        error: (err) => {
          console.error('Error updating service:', err);
          this.toastService.error(this.translateService.instant('companyProfile.addService.updateError'));
          this.saving = false;
        }
      });
    } else {
      this.endpointService.csr_2({
        company_id: this.company.company_id,
        service_name: formValue.service_name,
        service_description: formValue.service_description,
        price_cents: priceCents,
        currency: formValue.currency,
        vat_rate: formValue.vat_rate
      }).subscribe({
        next: () => {
          this.saving = false;
          this.toastService.success(this.translateService.instant('companyProfile.addService.success'));
          this.activeModal.close(true);
        },
        error: (err) => {
          console.error('Error creating service:', err);
          this.toastService.error(this.translateService.instant('companyProfile.addService.error'));
          this.saving = false;
        }
      });
    }
  }
}
